import { useEffect, useState } from "react";

/**
 * La tastiera del telefono e' aperta?
 *
 * SERVE A UNA COSA SOLA: sapere se lasciare, sotto la barra di scrittura, lo
 * spazio della home bar dell'iPhone. A tastiera chiusa serve (altrimenti la
 * barra finisce sotto la lineetta); a tastiera aperta no (la lineetta e'
 * coperta dalla tastiera), e quello spazio diventa un buco.
 *
 * PERCHE' E' STATO RISCRITTO (26/08/2026)
 * Davide: "quando entro in pwa, c'e' uno spazio in basso che si toglie allo
 * scroll, e' strano". Non era un capriccio del telefono: era questo interruttore
 * che si accendeva da solo. La versione precedente ascoltava anche lo SCROLL
 * della finestra visibile e teneva memoria dell'altezza massima mai vista; su
 * iOS, dove la pagina rimbalza a fine corsa, quell'altezza ballava di qualche
 * decina di pixel e la differenza superava la soglia. Risultato: l'app si
 * convinceva che fosse comparsa una tastiera, toglieva lo spazio della home bar
 * - e la barra di scrittura scivolava sotto la lineetta.
 *
 * DUE ERRORI POSSIBILI, MA NON SI EQUIVALGONO.
 * Dire "aperta" quando e' chiusa manda la barra sotto la home bar: un difetto
 * che si vede e che da' fastidio. Dire "chiusa" quando e' aperta lascia 34
 * pixel di troppo sopra la tastiera: nessuno se ne accorge. Quindi qui si
 * pretendono PROVE per dire "aperta", e nel dubbio si risponde "chiusa".
 *
 * La prova principale e' il fuoco su un campo di testo: lo scroll non sposta il
 * fuoco, quindi non puo' piu' accendere niente. Se poi il browser sa dire
 * quanto si e' ristretta la finestra visibile, quel restringimento deve avere
 * la dimensione di una tastiera vera - non i pochi pixel di un rimbalzo.
 */
export function useKeyboardOpen(threshold = 150): boolean {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const vv = window.visualViewport;

    const isMobileLike = () =>
      window.matchMedia("(pointer: coarse)").matches || window.innerWidth < 1024;

    const editableFocused = () => {
      const el = document.activeElement as HTMLElement | null;
      if (!el || typeof el.matches !== "function") return false;
      return el.matches("textarea, input, [contenteditable='true']");
    };

    const update = () => {
      if (!isMobileLike() || !editableFocused()) {
        setOpen(false);
        return;
      }
      // Quanto si e' ristretta la finestra visibile rispetto a quella del
      // documento. Nessuna memoria del passato: e' proprio il ricordo
      // dell'altezza massima che, sommato al rimbalzo dello scroll, faceva
      // scattare il falso allarme.
      const restringimento = vv ? Math.max(0, window.innerHeight - vv.height) : 0;
      // 0 = il browser non lo sa dire (capita in certe PWA): allora ci si fida
      // del fuoco. Un valore piccolo, invece, e' rumore: non e' una tastiera.
      setOpen(restringimento === 0 || restringimento > threshold);
    };

    const aggiornaDopo = () => window.setTimeout(update, 60);

    update();
    // Niente ascolto dello scroll: lo scroll non apre tastiere.
    vv?.addEventListener("resize", update);
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", aggiornaDopo);
    document.addEventListener("focusin", aggiornaDopo);
    document.addEventListener("focusout", aggiornaDopo);

    return () => {
      vv?.removeEventListener("resize", update);
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", aggiornaDopo);
      document.removeEventListener("focusin", aggiornaDopo);
      document.removeEventListener("focusout", aggiornaDopo);
    };
  }, [threshold]);

  return open;
}
